/**
 * RelatorioComissoesPendentes - Relatório detalhado de comissões pendentes
 * Similar ao sistema antigo: agrupamento por banco, operador, correspondente
 * Com drill-down para ver detalhes dos contratos
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Building, Users, DollarSign, Calendar, FileSpreadsheet, 
  ChevronDown, ChevronRight, AlertTriangle, Clock, Download, Upload,
  Printer, Eye, Filter, RefreshCw, Landmark, User, Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Função para calcular dias desde uma data
const calcularDias = (dataStr) => {
  if (!dataStr) return 0;
  const data = new Date(dataStr);
  const hoje = new Date();
  const diffTime = Math.abs(hoje - data);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Função para formatar moeda
const formatCurrency = (value) => {
  return `R$ ${parseFloat(value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
};

export default function RelatorioComissoesPendentes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contratos, setContratos] = useState([]);
  const [tabelasComissao, setTabelasComissao] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [correspondentes, setCorrespondentes] = useState([]);
  
  // Filtros
  const [filtroAgrupamento, setFiltroAgrupamento] = useState("banco");
  const [filtroBanco, setFiltroBanco] = useState("todos");
  const [filtroOperador, setFiltroOperador] = useState("todos");
  const [filtroCorrespondente, setFiltroCorrespondente] = useState("todos");
  const [filtroDiasMinimo, setFiltroDiasMinimo] = useState(0);
  
  // Estados de UI
  const [gruposExpandidos, setGruposExpandidos] = useState({});
  const [showDetalhe, setShowDetalhe] = useState(false);
  const [contratoSelecionado, setContratoSelecionado] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Carregar dados
  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [contratosRes, tabelasRes, bancosRes, correspRes] = await Promise.all([
        axios.get(`${API}/contratos`).catch(() => ({ data: { contratos: [] } })),
        axios.get(`${API}/tabelas-comissao`).catch(() => ({ data: { tabelas: [] } })),
        axios.get(`${API}/bancos`).catch(() => ({ data: [] })),
        axios.get(`${API}/correspondentes`).catch(() => ({ data: { correspondentes: [] } }))
      ]);
      
      setContratos(contratosRes.data?.contratos || contratosRes.data || []);
      setTabelasComissao(tabelasRes.data?.tabelas || []);
      setBancos(bancosRes.data || []);
      setCorrespondentes(correspRes.data?.correspondentes || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  // Filtrar contratos sem comissão paga
  const contratosPendentes = useMemo(() => {
    return contratos.filter(c => {
      const comissaoPaga = c.comissao_paga || c.comissao_recebida || false;
      const valor = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      const dias = calcularDias(c.data_contrato || c.created_at);
      
      // Filtros
      if (valor <= 0) return false;
      if (comissaoPaga) return false;
      if (filtroDiasMinimo > 0 && dias < filtroDiasMinimo) return false;
      if (filtroBanco !== "todos" && (c.banco || c.nome_banco || "Não informado") !== filtroBanco) return false;
      if (filtroOperador !== "todos" && (c.operador_comissao || c.vendedor || "Não informado") !== filtroOperador) return false;
      if (filtroCorrespondente !== "todos" && (c.correspondente || "Geral") !== filtroCorrespondente) return false;
      
      return true;
    }).map(c => ({
      ...c,
      dias_pendente: calcularDias(c.data_contrato || c.created_at),
      comissao_estimada: parseFloat(c.valor_liberado || c.valor_solicitado || 0) * 
        (parseFloat(c.comissao_calculada || c.taxa_comissao || 3) / 100)
    }));
  }, [contratos, filtroBanco, filtroOperador, filtroCorrespondente, filtroDiasMinimo]);

  // Agrupar contratos
  const contratosAgrupados = useMemo(() => {
    const grupos = {};
    
    contratosPendentes.forEach(c => {
      let chave;
      switch (filtroAgrupamento) {
        case "operador":
          chave = c.operador_comissao || c.vendedor || "Não informado";
          break;
        case "correspondente":
          chave = c.correspondente || "Geral";
          break;
        case "correspondente_banco":
          const corresp = c.correspondente || "Geral";
          const banco = c.banco || c.nome_banco || "Não informado";
          chave = `${corresp} | ${banco}`;
          break;
        default: // banco
          chave = c.banco || c.nome_banco || "Não informado";
      }
      
      if (!grupos[chave]) {
        grupos[chave] = {
          nome: chave,
          contratos: [],
          totalValor: 0,
          totalComissao: 0,
          qtdAlerta: 0 // contratos com mais de 7 dias
        };
      }
      
      grupos[chave].contratos.push(c);
      grupos[chave].totalValor += parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      grupos[chave].totalComissao += c.comissao_estimada;
      if (c.dias_pendente > 7) grupos[chave].qtdAlerta++;
    });
    
    return Object.values(grupos).sort((a, b) => b.totalComissao - a.totalComissao);
  }, [contratosPendentes, filtroAgrupamento]);

  // Totais gerais
  const totais = useMemo(() => {
    return {
      qtdContratos: contratosPendentes.length,
      valorTotal: contratosPendentes.reduce((acc, c) => acc + parseFloat(c.valor_liberado || c.valor_solicitado || 0), 0),
      comissaoTotal: contratosPendentes.reduce((acc, c) => acc + c.comissao_estimada, 0),
      qtdAlerta: contratosPendentes.filter(c => c.dias_pendente > 7).length
    };
  }, [contratosPendentes]);

  // Contratos base para filtros (todos os pendentes, sem aplicar filtros de seleção)
  const contratosPendentesBase = useMemo(() => {
    return contratos.filter(c => {
      const comissaoPaga = c.comissao_paga || c.comissao_recebida || false;
      const valor = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
      return valor > 0 && !comissaoPaga;
    });
  }, [contratos]);

  // Lista de BANCOS que têm contratos pendentes
  const bancosComPendentes = useMemo(() => {
    const bancoSet = new Set();
    contratosPendentesBase.forEach(c => {
      const banco = c.banco || c.nome_banco || "Não informado";
      bancoSet.add(banco);
    });
    return Array.from(bancoSet).sort();
  }, [contratosPendentesBase]);

  // Lista de OPERADORES que têm contratos pendentes
  const operadoresComPendentes = useMemo(() => {
    const opSet = new Set();
    contratosPendentesBase.forEach(c => {
      const op = c.operador_comissao || c.vendedor || "Não informado";
      opSet.add(op);
    });
    return Array.from(opSet).sort();
  }, [contratosPendentesBase]);

  // Lista de CORRESPONDENTES que têm contratos pendentes
  const correspondentesComPendentes = useMemo(() => {
    const corrSet = new Set();
    contratosPendentesBase.forEach(c => {
      const corr = c.correspondente || "Geral";
      corrSet.add(corr);
    });
    return Array.from(corrSet).sort();
  }, [contratosPendentesBase]);

  // Toggle grupo expandido
  const toggleGrupo = (nome) => {
    setGruposExpandidos(prev => ({
      ...prev,
      [nome]: !prev[nome]
    }));
  };

  // Exportar PDF
  const exportarPDF = () => {
    const doc = new jsPDF("landscape");
    
    doc.setFontSize(18);
    doc.text("Relatório de Comissões Pendentes", 14, 20);
    doc.setFontSize(10);
    doc.text(`Agrupado por: ${filtroAgrupamento.toUpperCase()}`, 14, 28);
    doc.text(`Data: ${new Date().toLocaleDateString("pt-BR")}`, 14, 34);
    doc.text(`Total: ${totais.qtdContratos} contratos | ${formatCurrency(totais.valorTotal)} | Comissão: ${formatCurrency(totais.comissaoTotal)}`, 14, 40);
    
    const tableData = [];
    contratosAgrupados.forEach(grupo => {
      // Linha do grupo
      tableData.push([
        { content: grupo.nome, colSpan: 7, styles: { fontStyle: 'bold', fillColor: [220, 220, 220] } },
        { content: formatCurrency(grupo.totalComissao), styles: { fontStyle: 'bold', fillColor: [220, 220, 220] } }
      ]);
      
      // Contratos do grupo
      grupo.contratos.forEach(c => {
        tableData.push([
          c.numero_contrato || c.codigo || c.id?.substring(0, 8) || "-",
          c.cliente_nome || c.cliente || "-",
          c.tipo_operacao || "-",
          formatCurrency(c.valor_liberado || c.valor_solicitado || 0),
          c.status || c.etapa || "Pendente",
          c.data_contrato ? new Date(c.data_contrato).toLocaleDateString("pt-BR") : "-",
          `${c.dias_pendente} dias`,
          formatCurrency(c.comissao_estimada)
        ]);
      });
    });
    
    doc.autoTable({
      head: [["Código", "Cliente", "Tipo", "Valor", "Status", "Data", "Dias Pend.", "Comissão"]],
      body: tableData,
      startY: 46,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [34, 139, 34] }
    });
    
    doc.save(`comissoes_pendentes_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("PDF exportado com sucesso!");
  };

  // Exportar Excel
  const exportarExcel = () => {
    const dados = [];
    
    contratosAgrupados.forEach(grupo => {
      grupo.contratos.forEach(c => {
        const valorLiberado = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
        const comissaoRecebidaValor = parseFloat(c.comissao_recebida_valor || 0);
        const comissaoRecebidaPerc = parseFloat(c.comissao_recebida_percentual || 0);
        const comissaoEsperada = c.comissao_estimada;
        const percentualEsperado = parseFloat(c.comissao_calculada || c.taxa_comissao || 3);
        
        // Calcular % recebido se só tiver valor
        const percRecebidoCalculado = comissaoRecebidaValor > 0 && valorLiberado > 0 
          ? (comissaoRecebidaValor / valorLiberado) * 100 
          : comissaoRecebidaPerc;
        
        const diferencaValor = comissaoRecebidaValor - comissaoEsperada;
        const diferencaPerc = percRecebidoCalculado - percentualEsperado;
        const correto = comissaoRecebidaValor > 0 && Math.abs(diferencaValor) < 0.01;
        
        dados.push({
          "Data Contrato": c.data_contrato ? new Date(c.data_contrato).toLocaleDateString("pt-BR") : "-",
          "Número do Contrato*": c.numero_contrato || c.codigo || c.id?.substring(0, 8) || "-",
          "Cliente*": c.cliente_nome || c.cliente || "-",
          "Banco*": c.banco || c.nome_banco || "-",
          "Instituição*": c.instituicao || "-",
          "Correspondente": c.correspondente || "Geral",
          "Tabela": c.tabela_comissao_nome || c.tabela || "-",
          "Produto/Nome": c.produto || c.tipo_operacao || "-",
          "Prazo*": c.prazo || "-",
          "Valor Solicitado Bruto": parseFloat(c.valor_solicitado || 0),
          "Valor Liberado*": valorLiberado,
          "Status": c.status || c.etapa || "Pendente",
          "Dias Pendente": c.dias_pendente,
          "Operador de Comissão*": c.operador_comissao || c.vendedor || "-",
          "% Esperado": percentualEsperado,
          "R$ Esperado": comissaoEsperada,
          "% Recebido": comissaoRecebidaValor > 0 ? percRecebidoCalculado : "",
          "R$ Recebido": comissaoRecebidaValor || "",
          "Correto (SIM/NÃO)": comissaoRecebidaValor > 0 ? (correto ? "SIM" : "NÃO") : "",
          "Diferença R$": comissaoRecebidaValor > 0 ? diferencaValor : "",
          "Diferença %": comissaoRecebidaValor > 0 ? diferencaPerc : ""
        });
      });
    });
    
    const ws = XLSX.utils.json_to_sheet(dados);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Comissões Pendentes");
    
    // Ajustar larguras
    ws["!cols"] = [
      { wch: 12 }, { wch: 18 }, { wch: 35 }, { wch: 15 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 8 }, { wch: 15 },
      { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 20 },
      { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }
    ];
    
    XLSX.writeFile(wb, `comissoes_pendentes_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Excel exportado com sucesso!");
  };

  // Importar Excel
  const importarExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        toast.success(`${jsonData.length} registros importados! (Funcionalidade de atualização em desenvolvimento)`);
        setShowImportDialog(false);
      } catch (error) {
        toast.error("Erro ao importar arquivo");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="relatorio-comissoes-pendentes">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-emerald-800">Comissões Pendentes de Recebimento</h1>
            <p className="text-sm text-slate-500">Contratos aprovados aguardando pagamento de comissão</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={carregarDados} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </Button>
          <Button onClick={() => navigate('/relatorios/vincular-comissoes')} className="gap-2 bg-purple-600 hover:bg-purple-700">
            <DollarSign className="h-4 w-4" />
            Vincular Comissões
          </Button>
          <Button variant="outline" onClick={() => setShowImportDialog(true)} className="gap-2 text-blue-600">
            <Upload className="h-4 w-4" />
            Importar
          </Button>
          <Button variant="outline" onClick={exportarExcel} className="gap-2 text-green-600">
            <FileSpreadsheet className="h-4 w-4" />
            Excel
          </Button>
          <Button variant="outline" onClick={exportarPDF} className="gap-2 text-red-600">
            <Download className="h-4 w-4" />
            PDF
          </Button>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-10 w-10 text-blue-600" />
              <div>
                <p className="text-3xl font-bold text-blue-800">{totais.qtdContratos}</p>
                <p className="text-sm text-blue-600">Contratos Pendentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-10 w-10 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-green-800">{formatCurrency(totais.valorTotal)}</p>
                <p className="text-sm text-green-600">Valor Total Liberado</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-10 w-10 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-800">{formatCurrency(totais.comissaoTotal)}</p>
                <p className="text-sm text-emerald-600">Comissão a Receber</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`bg-gradient-to-br ${totais.qtdAlerta > 0 ? 'from-red-50 to-red-100 border-red-200' : 'from-slate-50 to-slate-100 border-slate-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`h-10 w-10 ${totais.qtdAlerta > 0 ? 'text-red-600' : 'text-slate-400'}`} />
              <div>
                <p className={`text-3xl font-bold ${totais.qtdAlerta > 0 ? 'text-red-800' : 'text-slate-600'}`}>{totais.qtdAlerta}</p>
                <p className={`text-sm ${totais.qtdAlerta > 0 ? 'text-red-600' : 'text-slate-500'}`}>Pendentes +7 dias</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros e Agrupamento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-48">
              <Label className="text-xs">Agrupar por</Label>
              <Select value={filtroAgrupamento} onValueChange={setFiltroAgrupamento}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="banco">Por Banco</SelectItem>
                  <SelectItem value="operador">Por Operador de Comissão</SelectItem>
                  <SelectItem value="correspondente">Por Correspondente</SelectItem>
                  <SelectItem value="correspondente_banco">Por Correspondente + Banco</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs">Banco ({bancosComPendentes.length})</Label>
              <Select value={filtroBanco} onValueChange={setFiltroBanco}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos ({bancosComPendentes.length})</SelectItem>
                  {bancosComPendentes.map(banco => (
                    <SelectItem key={banco} value={banco}>{banco}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs">Operador ({operadoresComPendentes.length})</Label>
              <Select value={filtroOperador} onValueChange={setFiltroOperador}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos ({operadoresComPendentes.length})</SelectItem>
                  {operadoresComPendentes.map(op => (
                    <SelectItem key={op} value={op}>{op}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Label className="text-xs">Correspondente ({correspondentesComPendentes.length})</Label>
              <Select value={filtroCorrespondente} onValueChange={setFiltroCorrespondente}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos ({correspondentesComPendentes.length})</SelectItem>
                  {correspondentesComPendentes.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs">Dias mínimo pendente</Label>
              <Input 
                type="number" 
                value={filtroDiasMinimo} 
                onChange={e => setFiltroDiasMinimo(parseInt(e.target.value) || 0)}
                placeholder="0"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista Agrupada */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-emerald-100 to-teal-100">
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-700" />
            Comissões Pendentes - {filtroAgrupamento === "banco" ? "Por Banco" : 
              filtroAgrupamento === "operador" ? "Por Operador" : 
              filtroAgrupamento === "correspondente" ? "Por Correspondente" : "Por Correspondente/Banco"}
          </CardTitle>
          <CardDescription>
            Clique em cada grupo para ver os contratos detalhados
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {contratosAgrupados.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <DollarSign className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <p>Nenhum contrato pendente encontrado</p>
            </div>
          ) : (
            <div className="divide-y">
              {contratosAgrupados.map((grupo, idx) => (
                <Collapsible 
                  key={idx} 
                  open={gruposExpandidos[grupo.nome]}
                  onOpenChange={() => toggleGrupo(grupo.nome)}
                >
                  <CollapsibleTrigger className="w-full">
                    <div className={`flex items-center justify-between p-4 hover:bg-slate-50 cursor-pointer ${grupo.qtdAlerta > 0 ? 'bg-red-50' : ''}`}>
                      <div className="flex items-center gap-4">
                        {gruposExpandidos[grupo.nome] ? 
                          <ChevronDown className="h-5 w-5 text-slate-400" /> : 
                          <ChevronRight className="h-5 w-5 text-slate-400" />
                        }
                        <div className="text-left">
                          <p className="font-semibold text-lg">{grupo.nome}</p>
                          <p className="text-sm text-slate-500">
                            {grupo.contratos.length} contrato(s) | {formatCurrency(grupo.totalValor)}
                          </p>
                        </div>
                        {grupo.qtdAlerta > 0 && (
                          <Badge variant="destructive" className="gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {grupo.qtdAlerta} pendente(s) +7 dias
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-emerald-700">{formatCurrency(grupo.totalComissao)}</p>
                        <p className="text-xs text-slate-500">Comissão a receber</p>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="bg-slate-50 p-4 border-t overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-emerald-100">
                            <TableHead className="font-bold text-xs">Data Contrato</TableHead>
                            <TableHead className="font-bold text-xs">Nº Contrato</TableHead>
                            <TableHead className="font-bold text-xs">Cliente</TableHead>
                            <TableHead className="font-bold text-xs">Banco</TableHead>
                            <TableHead className="font-bold text-xs">Instituição</TableHead>
                            <TableHead className="font-bold text-xs">Correspondente</TableHead>
                            <TableHead className="font-bold text-xs">Tabela</TableHead>
                            <TableHead className="font-bold text-xs">Produto/Nome</TableHead>
                            <TableHead className="font-bold text-xs text-center">Prazo</TableHead>
                            <TableHead className="font-bold text-xs text-right">Valor Solicitado</TableHead>
                            <TableHead className="font-bold text-xs text-right">Valor Liberado</TableHead>
                            <TableHead className="font-bold text-xs text-center">Status</TableHead>
                            <TableHead className="font-bold text-xs text-center">Dias Pend.</TableHead>
                            <TableHead className="font-bold text-xs">Operador</TableHead>
                            <TableHead className="font-bold text-xs text-center bg-yellow-100">% Esperado</TableHead>
                            <TableHead className="font-bold text-xs text-right bg-yellow-100">R$ Esperado</TableHead>
                            <TableHead className="font-bold text-xs text-center bg-green-100">% Recebido</TableHead>
                            <TableHead className="font-bold text-xs text-right bg-green-100">R$ Recebido</TableHead>
                            <TableHead className="font-bold text-xs text-center bg-purple-100">Correto?</TableHead>
                            <TableHead className="font-bold text-xs text-right bg-red-100">Diferença R$</TableHead>
                            <TableHead className="font-bold text-xs text-center bg-red-100">Diferença %</TableHead>
                            <TableHead className="font-bold text-xs text-center">Ação</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {grupo.contratos.map((c, cIdx) => {
                            const valorLiberado = parseFloat(c.valor_liberado || c.valor_solicitado || 0);
                            const comissaoRecebidaValor = parseFloat(c.comissao_recebida_valor || 0);
                            const comissaoRecebidaPerc = parseFloat(c.comissao_recebida_percentual || 0);
                            const comissaoEsperada = c.comissao_estimada;
                            const percentualEsperado = parseFloat(c.comissao_calculada || c.taxa_comissao || 3);
                            
                            // Calcular % recebido se só tiver valor
                            const percRecebidoCalculado = comissaoRecebidaValor > 0 && valorLiberado > 0 
                              ? (comissaoRecebidaValor / valorLiberado) * 100 
                              : comissaoRecebidaPerc;
                            
                            const diferencaValor = comissaoRecebidaValor - comissaoEsperada;
                            const diferencaPerc = percRecebidoCalculado - percentualEsperado;
                            const correto = comissaoRecebidaValor > 0 && Math.abs(diferencaValor) < 0.01;
                            
                            return (
                              <TableRow 
                                key={cIdx} 
                                className={c.dias_pendente > 7 ? 'bg-red-50' : 'hover:bg-white'}
                              >
                                <TableCell className="text-xs">
                                  {c.data_contrato ? new Date(c.data_contrato).toLocaleDateString("pt-BR") : "-"}
                                </TableCell>
                                <TableCell className="font-medium text-xs">
                                  {c.numero_contrato || c.codigo || c.id?.substring(0, 8) || "-"}
                                </TableCell>
                                <TableCell className="text-xs max-w-[150px] truncate" title={c.cliente_nome || c.cliente}>
                                  {c.cliente_nome || c.cliente || "-"}
                                </TableCell>
                                <TableCell className="text-xs">{c.banco || c.nome_banco || "-"}</TableCell>
                                <TableCell className="text-xs">{c.instituicao || "-"}</TableCell>
                                <TableCell className="text-xs">{c.correspondente || "Geral"}</TableCell>
                                <TableCell className="text-xs">{c.tabela_comissao_nome || c.tabela || "-"}</TableCell>
                                <TableCell className="text-xs">{c.produto || c.tipo_operacao || "-"}</TableCell>
                                <TableCell className="text-center text-xs">{c.prazo || "-"}</TableCell>
                                <TableCell className="text-right text-xs">
                                  {formatCurrency(c.valor_solicitado || 0)}
                                </TableCell>
                                <TableCell className="text-right text-xs font-medium">
                                  {formatCurrency(valorLiberado)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="secondary" className="text-xs">{c.status || c.etapa || "Pendente"}</Badge>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={c.dias_pendente > 7 ? "destructive" : "outline"} className="text-xs">
                                    {c.dias_pendente}d
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs">{c.operador_comissao || c.vendedor || "-"}</TableCell>
                                <TableCell className="text-center text-xs bg-yellow-50 font-medium">
                                  {percentualEsperado.toFixed(2)}%
                                </TableCell>
                                <TableCell className="text-right text-xs bg-yellow-50 font-medium">
                                  {formatCurrency(comissaoEsperada)}
                                </TableCell>
                                <TableCell className="text-center text-xs bg-green-50 font-medium">
                                  {comissaoRecebidaValor > 0 ? `${percRecebidoCalculado.toFixed(2)}%` : "-"}
                                </TableCell>
                                <TableCell className="text-right text-xs bg-green-50 font-medium">
                                  {comissaoRecebidaValor > 0 ? formatCurrency(comissaoRecebidaValor) : "-"}
                                </TableCell>
                                <TableCell className="text-center bg-purple-50">
                                  {comissaoRecebidaValor > 0 ? (
                                    <Badge variant={correto ? "default" : "destructive"} className={`text-xs ${correto ? 'bg-green-600' : ''}`}>
                                      {correto ? "SIM" : "NÃO"}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-slate-400">-</span>
                                  )}
                                </TableCell>
                                <TableCell className={`text-right text-xs bg-red-50 font-medium ${diferencaValor < 0 ? 'text-red-600' : diferencaValor > 0 ? 'text-green-600' : ''}`}>
                                  {comissaoRecebidaValor > 0 ? (
                                    diferencaValor >= 0 ? `+${formatCurrency(diferencaValor)}` : formatCurrency(diferencaValor)
                                  ) : "-"}
                                </TableCell>
                                <TableCell className={`text-center text-xs bg-red-50 font-medium ${diferencaPerc < 0 ? 'text-red-600' : diferencaPerc > 0 ? 'text-green-600' : ''}`}>
                                  {comissaoRecebidaValor > 0 ? (
                                    diferencaPerc >= 0 ? `+${diferencaPerc.toFixed(2)}%` : `${diferencaPerc.toFixed(2)}%`
                                  ) : "-"}
                                </TableCell>
                                <TableCell className="text-center">
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="text-blue-600 h-7 w-7 p-0"
                                    onClick={() => {
                                      setContratoSelecionado(c);
                                      setShowDetalhe(true);
                                    }}
                                  >
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rodapé com Total */}
      <Card className="bg-emerald-800 text-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <DollarSign className="h-8 w-8" />
              <div>
                <p className="text-sm opacity-80">TOTAL GERAL A RECEBER</p>
                <p className="text-3xl font-bold">{formatCurrency(totais.comissaoTotal)}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm opacity-80">{totais.qtdContratos} contratos</p>
              <p className="text-lg">Valor: {formatCurrency(totais.valorTotal)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog de Detalhe do Contrato */}
      <Dialog open={showDetalhe} onOpenChange={setShowDetalhe}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Detalhes do Contrato</DialogTitle>
            <DialogDescription>
              Informações completas do contrato pendente de comissão
            </DialogDescription>
          </DialogHeader>
          {contratoSelecionado && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Código</Label>
                  <p className="font-medium">{contratoSelecionado.numero_contrato || contratoSelecionado.codigo || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Cliente</Label>
                  <p className="font-medium">{contratoSelecionado.cliente_nome || contratoSelecionado.cliente || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Banco</Label>
                  <p className="font-medium">{contratoSelecionado.banco || contratoSelecionado.nome_banco || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Tipo de Operação</Label>
                  <p className="font-medium">{contratoSelecionado.tipo_operacao || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Valor Liberado</Label>
                  <p className="font-medium text-lg">{formatCurrency(contratoSelecionado.valor_liberado || contratoSelecionado.valor_solicitado)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <p className="font-medium">{contratoSelecionado.status || contratoSelecionado.etapa || "Pendente"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Data do Contrato</Label>
                  <p className="font-medium">{contratoSelecionado.data_contrato ? new Date(contratoSelecionado.data_contrato).toLocaleDateString("pt-BR") : "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Dias Pendente</Label>
                  <Badge variant={contratoSelecionado.dias_pendente > 7 ? "destructive" : "outline"} className="mt-1">
                    <Clock className="h-3 w-3 mr-1" />
                    {contratoSelecionado.dias_pendente} dias
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Operador de Comissão</Label>
                  <p className="font-medium">{contratoSelecionado.operador_comissao || contratoSelecionado.vendedor || "-"}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Correspondente</Label>
                  <p className="font-medium">{contratoSelecionado.correspondente || "Geral"}</p>
                </div>
              </div>
              
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <Label className="text-xs text-emerald-700">COMISSÃO ESTIMADA</Label>
                <p className="text-3xl font-bold text-emerald-700">{formatCurrency(contratoSelecionado.comissao_estimada)}</p>
              </div>
              
              {contratoSelecionado.dias_pendente > 7 && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200 flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-700">ATENÇÃO!</p>
                    <p className="text-sm text-red-600">Comissão pendente por mais de 7 dias!</p>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetalhe(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Importação */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Importar Dados
            </DialogTitle>
            <DialogDescription>
              Importe uma planilha Excel com dados de comissões pagas
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-4" />
              <p className="text-slate-600 mb-4">Arraste um arquivo ou clique para selecionar</p>
              <input 
                type="file" 
                accept=".xlsx,.xls" 
                onChange={importarExcel}
                className="hidden"
                id="import-file"
              />
              <label htmlFor="import-file">
                <Button asChild variant="outline">
                  <span>Selecionar Arquivo</span>
                </Button>
              </label>
            </div>
            <p className="text-xs text-slate-500">
              O arquivo deve conter as colunas: Código, Data Pagamento, Valor Comissão
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
